import { define, debounce, parseHTML } from '@theme/global';

/* <predictive-search data-enabled="true">
   子要素:
   - [data-predictive-input]    : 検索入力（role="combobox"）
   - [data-predictive-results]  : 結果の差し込み先（role="listbox"）
   - [data-predictive-default]  : 未入力時に出す内容（よく検索されるワード）
   結果は sections/predictive-search.liquid を section rendering で取得する */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.defaults = this.querySelector('[data-predictive-default]');
    this.form = this.querySelector('form');
    if (this.dataset.enabled !== 'true' || !this.input || !this.results) return;

    this.cache = new Map();
    this.input.addEventListener('input', debounce(() => this.search(), 250));
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.form?.addEventListener('submit', (e) => {
      const active = this.activeOption;
      if (active) {
        e.preventDefault();
        active.click();
      }
    });
    this.results.addEventListener('pointermove', (e) => {
      const option = e.target.closest('[data-predictive-option]');
      if (option) this.select(this.options.indexOf(option), false);
    });
    if (this.term) this.search();
  }

  get term() {
    return this.input.value.trim();
  }

  get options() {
    return [...this.results.querySelectorAll('[data-predictive-option]')];
  }

  get activeOption() {
    return this.results.querySelector('[data-predictive-option][aria-selected="true"]');
  }

  async search() {
    const q = this.term;
    if (!q) return this.reset();
    if (this.cache.has(q)) return this.render(this.cache.get(q));

    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    try {
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search',
      });
      const res = await fetch(`${window.theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
      if (!res.ok) throw new Error(`Predictive search failed: ${res.status}`);
      const html = parseHTML(await res.text()).querySelector('[data-predictive-content]')?.outerHTML || '';
      this.cache.set(q, html);
      if (q === this.term) this.render(html);
    } catch (e) {
      if (e.name !== 'AbortError') this.reset();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  render(html) {
    this.results.innerHTML = html;
    this.options.forEach((option, i) => {
      option.id = `PredictiveOption-${i}`;
      option.setAttribute('aria-selected', 'false');
    });
    const hasResults = html !== '';
    this.results.hidden = !hasResults;
    if (this.defaults) this.defaults.hidden = hasResults;
    this.input.setAttribute('aria-expanded', String(hasResults));
    this.input.removeAttribute('aria-activedescendant');
  }

  reset() {
    this.controller?.abort();
    this.results.innerHTML = '';
    this.results.hidden = true;
    if (this.defaults) this.defaults.hidden = false;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  select(index, scroll = true) {
    const options = this.options;
    options.forEach((o, i) => o.setAttribute('aria-selected', String(i === index)));
    const option = options[index];
    if (!option) return this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-activedescendant', option.id);
    if (scroll) option.scrollIntoView({ block: 'nearest' });
  }

  onKeydown(e) {
    if (e.key === 'Escape' && this.term) {
      // 1回目の Esc は入力をクリア、2回目でドロワーを閉じる
      e.preventDefault();
      e.stopPropagation();
      this.input.value = '';
      this.reset();
      return;
    }
    const options = this.options;
    if (!options.length || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    e.preventDefault();
    const current = options.indexOf(this.activeOption);
    const next = e.key === 'ArrowDown'
      ? (current + 1) % options.length
      : (current - 1 + options.length) % options.length;
    this.select(next);
  }
}
define('predictive-search', PredictiveSearch);
